import { resolveCssVar } from "@appretio/shared";
import { semanticThemeTokens } from "@appretio/ui-kit";
import { ColDef } from "ag-grid-enterprise";
import { createNumericColumnProps } from "../reviewDataTable/utils/columnProps";
import { DataTableImageWithDialog } from "./cellRendererComponents/DataTableImageWithDialog";

export function createColumnDefinitions(): ColDef[] {
  return [
    {
      headerName: "Image",
      field: "appretio_thumbnail_url",
      cellRenderer: DataTableImageWithDialog,
      minWidth: 100
    },
    {
      headerName: "Style Option Number",
      field: "style_option_number",
      initialHide: true,
      flex: 2,
      minWidth: 110
    },
    {
      headerName: "Style Option Id",
      field: "style_option_id_mockup",
      flex: 2,
      minWidth: 185
    },
    {
      headerName: "Style Number",
      field: "style_number",
      minWidth: 160
    },
    {
      headerName: "Style Name",
      field: "style_name",
      flex: 3,
      minWidth: 155
    },
    {
      headerName: "Variant Name",
      field: "style_variant_name",
      flex: 2,
      minWidth: 125
    },
    {
      headerName: "Colour",
      field: "style_colour",
      flex: 2,
      minWidth: 115
    },
    {
      headerName: "Hex Code",
      field: "style_colour_hex_code",
      minWidth: 130
    },
    {
      headerName: "RRP EUR",
      field: "style_recommended_retail_price_eur",
      minWidth: 125,
      ...createNumericColumnProps(2)
    },
    {
      headerName: "Seasonality",
      field: "seasonality",
      flex: 2,
      minWidth: 170,
      headerTooltip: "Yearly seasonality curve",
      cellRenderer: "agSparklineCellRenderer",
      cellRendererParams: {
        sparklineOptions: {
          type: "line",
          xKey: "week",
          yKey: "seasonality",
          stroke: resolveCssVar(
            semanticThemeTokens.sparklineGraph.seasonality.stroke
          ),
          strokeWidth: 2,
          padding: { top: 10, bottom: 10 },
          tooltip: {
            renderer: (params: { yValue: number; xValue: number }) => {
              return {
                title: "",
                content: `week: ${(params.xValue + 1).toFixed(0)}, ${params.yValue.toFixed(1)}`
              };
            }
          }
        }
      },
      valueGetter: (params) =>
        params.data?.seasonality ? params.data.seasonality : [] // necessary to format null values from BE since ag-Grid treats them incorrectly
    },
    {
      headerName: "Trend",
      field: "trend",
      flex: 2,
      minWidth: 170,
      headerTooltip: "Trend curve",
      cellRenderer: "agSparklineCellRenderer",
      cellRendererParams: {
        sparklineOptions: {
          type: "line",
          xKey: "week",
          yKey: "trend",
          stroke: resolveCssVar(
            semanticThemeTokens.sparklineGraph.trend.stroke
          ),
          strokeWidth: 2,
          padding: { top: 10, bottom: 10 },
          tooltip: {
            renderer: (params: { yValue: number; xValue: number }) => {
              return {
                title: "",
                content: `week: ${(params.xValue + 1).toFixed(0)}, ${params.yValue.toFixed(1)}`
              };
            }
          }
        }
      },
      valueGetter: (params) => (params?.data?.trend ? params.data.trend : []) // necessary to format null values from BE since ag-Grid treats them incorrectly
    },
    {
      headerName: "GS I (OD)",
      field: "gs1",
      headerTooltip:
        "Gross Sales (in value) before any discounts (on order date)",
      initialHide: true,
      minWidth: 100,
      ...createNumericColumnProps(2)
    },
    {
      headerName: "GIS I (OD)",
      field: "gis1",
      initialHide: true,
      minWidth: 110,
      ...createNumericColumnProps(0)
    },
    {
      headerName: "NS III (OD)",
      field: "ns3",
      headerTooltip: "Net Sales: GS II (OD) - GR II (OD)",
      initialHide: true,
      minWidth: 120,
      ...createNumericColumnProps(0)
    },
    {
      headerName: "GS II (OD)",
      field: "gs2",
      headerTooltip:
        "Gross Sales (in value) after any discounts (on order date)",
      initialHide: true,
      minWidth: 110,
      ...createNumericColumnProps(0)
    },
    {
      headerName: "NIS III (OD)",
      field: "nis3",
      headerTooltip: "GIS I - GIR - Net Items Sales",
      initialHide: true,
      minWidth: 130,
      ...createNumericColumnProps(0)
    },
    {
      headerName: "GR II (OD)",
      field: "gr2",
      headerTooltip: "Value of goods returned",
      initialHide: true,
      minWidth: 120,
      ...createNumericColumnProps(2)
    },
    {
      headerName: "GIR (OD)",
      field: "gir",
      initialHide: true,
      minWidth: 95,
      ...createNumericColumnProps(2)
    },
    {
      headerName: "ST",
      field: "st",
      initialHide: true,
      ...createNumericColumnProps(2)
    },
    {
      headerName: "TIME PURCHASE",
      field: "time_purchase",
      initialHide: true,
      minWidth: 105,
      ...createNumericColumnProps(0)
    },
    {
      headerName: "POTENTIAL NIS",
      field: "potential_nis",
      initialHide: true,
      minWidth: 170,
      ...createNumericColumnProps(0)
    },
    {
      headerName: "RANK",
      field: "rank",
      initialHide: true,
      minWidth: 100
    }
  ] satisfies ColDef[];
}
